import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { apiLinks } from "../core/api-links/api-links";

export interface FingerprintCredential {
  id: string;
  type: string;
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject: ArrayBuffer;
  };
}

export interface FingerprintRegistrationData {
  userId: string;
  credentialId: string;
  publicKey: string;
  signCount: number;
  deviceInfo?: any;
}

export interface DeviceInfo {
  browserVersion: string;
  browserName: string;
  plugIn: string;
  userAgent: string;
  timeZone: string;
  osName: string;
  platform: string;
  language: string;
  hardwareConcurrency: number;
  cookies: string;
  longitude: string;
  latitude: string;
}

@Injectable({
  providedIn: 'root'
})
export class PasskeyService {
  private readonly baseUrl = `${environment.apiUrl}/passkey`;

  constructor(private readonly http: HttpClient, private readonly handleError: ErrorHandlerService,
   ) {}

  /**
   * Check if WebAuthn is supported by the browser
   */
  isSupported(): boolean {
    return window.PublicKeyCredential !== undefined;
  }

  /**
   * Check if biometric authentication is available
   */
  async isBiometricAvailable(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return false;
    }
  }

  /**
   * Get device information for fingerprint registration
   */
 getDeviceInfo(): DeviceInfo {
  return {
    // browserVersion: "123.0.0.0", // Keep fixed for consistency unless the backend truly tracks minor updates
    browserVersion: this.getBrowserVersion(), // Using actual version is fine, but ensure backend logic is flexible
    browserName: this.getBrowserName(),
    plugIn: "Adobe Flash Player",
    userAgent: navigator.userAgent,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Use a more reliable way to get timeZone
    osName: this.getOSNameFromUA(navigator.userAgent),
    platform: navigator.platform, 
    language: navigator.language, 
    hardwareConcurrency: navigator.hardwareConcurrency || 0, 
    cookies: "true",
    longitude: "-73.9857",
    latitude: "40.7484"
  };
}

// Add a helper to get the browser version reliably
private getBrowserVersion(): string {
  const userAgent = navigator.userAgent;
  const match = userAgent.match(/(?:firefox|chrome|crios|safari|edge|opr)\/(\d+(\.\d+)*)/i);
  return match ? match[1] : 'unknown';
}

  /**
   * Get browser name from user agent
   */
  private getBrowserName(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Opera') || userAgent.includes('Opr')) {
      return 'Opera';
    } else if (userAgent.includes('Edg')) {
      return 'Edge';
    } else if (userAgent.includes('Chrome')) {
      return 'Chrome';
    } else if (userAgent.includes('Safari')) {
      return 'Safari';
    } else if (userAgent.includes('Firefox')) {
      return 'Firefox';
    } else {
      return 'unknown';
    }
  }

  // Derive a stable OS name across browsers for device fingerprinting
  private getOSNameFromUA(userAgent: string): string {
    try {
      if (/Windows/i.test(userAgent)) return 'Windows';
      if (/Mac OS X|Macintosh/i.test(userAgent)) return 'macOS';
      if (/Linux/i.test(userAgent)) return 'Linux';
      if (/Android/i.test(userAgent)) return 'Android';
      if (/iPhone|iPad|iOS/i.test(userAgent)) return 'iOS';
      return 'UnknownOS';
    } catch {
      return 'UnknownOS';
    }
  }

  /**
   * Register fingerprint with MFA verification
   */

async registerFingerprint(challengeData,challenge = {}) {
  // getRegistrationChallenge returns { publicKey, challengeId }
  let res :any;
  if(!challenge){
    res = await this.getRegistrationChallenge();
  }else {
    res = challengeData.data
  }
  const options: any = res?.publicKey;
  const challengeId: string = res?.challengeId;
  if (!options || !challengeId) throw new Error('Invalid registration options');

  // Convert base64url -> ArrayBuffers
  options.challenge = this.base64UrlToBuffer(options.challenge);
  options.user.id = this.base64UrlToBuffer(options.user.id);

  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map((cred: any) => ({
      ...cred,
      id: this.base64UrlToBuffer(cred.id),
    }));
  }

  const credential = await navigator.credentials.create({ publicKey: options }) as PublicKeyCredential | null;
  console.log("🚀 ~ PasskeyService ~ registerFingerprint ~ credential:", credential)
  if (!credential) throw new Error('Credential creation failed');

  const payload = {
    id: credential.id,
    rawId: this.bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: this.bufferToBase64Url((credential.response as AuthenticatorAttestationResponse).attestationObject),
      clientDataJSON: this.bufferToBase64Url((credential.response as AuthenticatorAttestationResponse).clientDataJSON),
    },
    deviceInfo: this.getDeviceInfo(),
    transports: ["internal"],
    challengeId 
  };

  const result = await this.postCommonHttpClient(apiLinks.passkey.verifyRegistration, payload).toPromise();
  return result;
}

async authenticateWithFingerprint(email: string): Promise<any> {
  try {
    const challengeRes: any = await this.getAuthenticationChallenge(email || null);

    if (!challengeRes?.data?.publicKey || !challengeRes?.data?.challengeId) {
      throw new Error("Invalid authentication challenge received from server");
    }

    const opts = challengeRes.data.publicKey;
    const userId = challengeRes.data.userId;
    const challengeId = challengeRes.data.challengeId;

    const challengeBuffer = this.base64UrlToBuffer(opts.challenge);
    if (!challengeBuffer?.byteLength) throw new Error("Failed to convert challenge to ArrayBuffer");

    let allowCredentials: any[] = [];
    if (opts.allowCredentials?.length) {
      allowCredentials = opts.allowCredentials.map((cred: any) => {
        try {
          const idBuffer = this.base64UrlToBuffer(cred.id);
          if (!idBuffer?.byteLength) return null;
          return { id: idBuffer, type: cred.type || "public-key" };
        } catch {
          return null;
        }
      }).filter(Boolean);
    }

    const rpId = (opts.rpId || window.location.hostname.split(":")[0]).toLowerCase();

    const assertionOptions: PublicKeyCredentialRequestOptions = {
      challenge: challengeBuffer,
      rpId,
      timeout: opts.timeout || 60000,
      userVerification: "required",
      ...(allowCredentials.length > 0 ? { allowCredentials } : {}),
    };

    const assertion: PublicKeyCredential = await navigator.credentials.get({ publicKey: assertionOptions }) as PublicKeyCredential;
    if (!assertion) throw new Error("Authentication cancelled");

    const verificationPayload = {
      assertion: {
        id: assertion.id,
        rawId: this.bufferToBase64Url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: this.bufferToBase64Url((assertion.response as AuthenticatorAssertionResponse).authenticatorData),
          clientDataJSON: this.bufferToBase64Url((assertion.response as AuthenticatorAssertionResponse).clientDataJSON),
          signature: this.bufferToBase64Url((assertion.response as AuthenticatorAssertionResponse).signature),
          userHandle: this.bufferToBase64Url((assertion.response as AuthenticatorAssertionResponse).userHandle),
        }
      },
      // include userId (may be null) and the challengeId so server can find the correct stored challenge
      userId: userId || null,
      email: email || null,
      challengeId, // <--- IMPORTANT
      isPasskeyLogin: true,
      fingerPrintData: this.getDeviceInfo(),
      version: environment.version,
      whitelabelData: JSON.parse(localStorage.getItem("whitelabelResData") || 'null'),
    };

    const verificationRes: any = await this.postCommonHttpClient(apiLinks.passkey.verifyAuthentication, verificationPayload).toPromise();
    return verificationRes;

  } catch (error: any) {
    console.error("authenticateWithFingerprint() Error:", error);
    if (error?.name === "NotAllowedError") throw new Error("Authentication cancelled or no passkey selected");
    if (error?.name === "NotSupportedError") throw new Error("Passkey authentication is not supported on this device");
    throw new Error(error?.message || "Passkey authentication failed. Please try again.");
  }
}

  getUserDevices(): Observable<any> {
    return this.http.get(`${this.baseUrl}/user-devices`);
  }


  /**
   * Get registration challenge from server
   */
  async getRegistrationChallenge(){
     const res: any = await this.postCommonHttpClient(apiLinks.passkey.registerChallenge, {}).toPromise();
     return res.data;
  }

  /**
   * Get authentication challenge from server
   */
  async getAuthenticationChallenge(email: string) {
    const deviceInfo = this.getDeviceInfo();
    const res: any = await this.postCommonHttpClient(apiLinks.passkey.authenticationChallenge, { email, deviceInfo }).toPromise();
    return res
  }


  /**
   * Check if there are any registered credentials on the server for the current domain
   * This method makes a server call and doesn't trigger browser prompts
   */
  async checkServerForCredentials(email = ''): Promise<{hasCredential: boolean, canShowPasskey: boolean, exclueUserType : []}> {
    try {
      const deviceInfo = this.getDeviceInfo();
      const res: any = await this.postCommonHttpClient(apiLinks.passkey.verifyPasskeyUsers, { email, deviceInfo }).toPromise();
      return {
      hasCredential: !!res?.data?.hasCredential,
      canShowPasskey: !!res?.data?.canShowPasskey,
      exclueUserType : res?.data?.exclueUserType
    };
    } catch (error) {
      console.error('Error checking server for credentials:', error);
      return {
      hasCredential: false,
      canShowPasskey: false,
      exclueUserType: [],
    };
    }
  }

  /**
   * Delete fingerprint for user
   */
  deleteFingerprint(deviceId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/delete/${deviceId}`);
  }

  base64UrlToBuffer(base64urlString: string): ArrayBuffer {
    try {
      if (!base64urlString || typeof base64urlString !== 'string') {
        throw new Error('Invalid base64url string provided');
      }
      
      // Convert base64url to base64
      let base64 = base64urlString.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if needed
      const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
      base64 = base64 + pad;
      
      // Decode base64 to binary string
      const binary = atob(base64);
      
      // Convert binary string to Uint8Array
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      
      return bytes.buffer;
    } catch (error) {
      console.error('❌ Error in base64UrlToBuffer:', error, 'Input:', base64urlString);
      throw new Error(`Failed to convert base64url to ArrayBuffer: ${error.message}`);
    }
  }


bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  const base64 = btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return base64;
}

  postCommonHttpClient(url, data, httpOptions = {}) {
      return this.http.post(url, data, httpOptions).pipe(
        map((res: any) => {
          return res;
        }),
        catchError(this.handleError.handleError)
      );
    }

} 